import { collector } from "../collector";
import { SERVER_HOST } from "../../constants/config";

// Measures connectivity AND stability. navigator.onLine / Network Information API
// are best-effort hints (Chromium-only for connection); the cross-browser source
// of truth is a lightweight periodic heartbeat to our own /ekyc/diagnostics/ping
// (204), from which we derive RTT / jitter / packet loss / drops.

const PING_INTERVAL_MS = 15000;
const PING_TIMEOUT_MS = 5000;
const MAX_SAMPLES = 60;

function connection() {
  return navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
}

function snapshot() {
  const c = connection();
  return {
    online: navigator.onLine,
    effectiveType: (c && c.effectiveType) || null,
    downlink: c && typeof c.downlink === "number" ? c.downlink : null,
    rtt: c && typeof c.rtt === "number" ? c.rtt : null,
    saveData: c ? !!c.saveData : null,
    type: (c && c.type) || null,
  };
}

export function startConnectivity() {
  const samples = [];
  let online = navigator.onLine;
  let dropCount = 0;
  let reconnectCount = 0;
  let lastOfflineAt = null;
  let totalOfflineMs = 0;
  let longestOfflineMs = 0;
  let timer = null;

  const rawFetch = (window.__diagOriginals && window.__diagOriginals.fetch) || window.fetch;

  function computeStability() {
    const total = samples.length;
    const fails = samples.filter((s) => !s.ok).length;
    const rtts = samples.filter((s) => s.ok && typeof s.rtt === "number").map((s) => s.rtt);
    let jitter = 0;
    for (let i = 1; i < rtts.length; i++) jitter += Math.abs(rtts[i] - rtts[i - 1]);
    jitter = rtts.length > 1 ? Math.round(jitter / (rtts.length - 1)) : 0;
    const avg = rtts.length ? Math.round(rtts.reduce((a, b) => a + b, 0) / rtts.length) : null;
    const sorted = rtts.slice().sort((a, b) => a - b);
    const p95 = sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] : null;
    const packetLossPct = total ? Math.round((fails / total) * 1000) / 10 : 0;

    let quality = "stable";
    if (packetLossPct > 5 || longestOfflineMs > 10000) quality = "poor";
    else if (jitter > 150 || packetLossPct > 2 || dropCount >= 2) quality = "unstable";

    return {
      pingCount: total,
      pingFailCount: fails,
      packetLossPct,
      rttAvgMs: avg,
      rttP95Ms: p95,
      jitterMs: jitter,
      dropCount,
      reconnectCount,
      longestOfflineMs,
      totalOfflineMs,
      connectionQuality: quality,
    };
  }

  function emitSummary() {
    collector.track("connectivity_summary", { ...snapshot(), ...computeStability() });
  }

  async function ping() {
    // Pause heartbeat while backgrounded so a background tab isn't scored as a drop.
    if (document.visibilityState === "hidden") return;
    const t0 = (window.performance && performance.now()) || Date.now();
    const controller = new AbortController();
    const to = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    let ok = false;
    let rtt = null;
    try {
      await rawFetch(`${SERVER_HOST}/ekyc/diagnostics/ping?t=${Date.now()}`, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      });
      rtt = Math.round(((window.performance && performance.now()) || Date.now()) - t0);
      ok = true;
    } catch (e) {
      ok = false;
    } finally {
      clearTimeout(to);
    }
    samples.push({ ok, rtt });
    if (samples.length > MAX_SAMPLES) samples.shift();
    emitSummary();
  }

  const onOffline = () => {
    if (online) {
      online = false;
      lastOfflineAt = Date.now();
      dropCount += 1;
      collector.track("connectivity_change", { online: false, ...snapshot() });
    }
  };
  const onOnline = () => {
    if (!online) {
      online = true;
      reconnectCount += 1;
      if (lastOfflineAt) {
        const d = Date.now() - lastOfflineAt;
        totalOfflineMs += d;
        if (d > longestOfflineMs) longestOfflineMs = d;
        lastOfflineAt = null;
      }
      collector.track("connectivity_change", { online: true, ...snapshot() });
    }
  };
  const onConnChange = () => collector.track("connectivity_change", snapshot());

  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  const c = connection();
  if (c && c.addEventListener) c.addEventListener("change", onConnChange);

  timer = setInterval(ping, PING_INTERVAL_MS);
  ping();

  return () => {
    if (timer) clearInterval(timer);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    if (c && c.removeEventListener) c.removeEventListener("change", onConnChange);
  };
}
